'use client'

import { useCallback } from 'react'

import { FormattedMessage, useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { Switch } from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'

import type {
  BooleanFilterKey,
  ListingFiltersState,
} from '@/components/listings/search/ListingSearchProvider'

import { useTrackFilterUsage } from '../shared/analytics'
import AppliedFilterSection from '../shared/AppliedFilterSection'
import type { AppliedProps, FilterProps, ListingFilter } from '../types'

const ScaledSwitch = styled(Switch)`
  transform: scale(0.75);
  transform-origin: left center;
`

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

const ToggleLabel = styled.span`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.black};
`

const HelperText = styled.p`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
  margin: 0.5rem 0 0;
`

export default function buildBooleanFilter({
  key,
  filterTitle,
}: {
  key: BooleanFilterKey
  filterTitle: string
}) {
  const label = `Filters.${key}`
  const toggleLabelId = `Filters.${key}.label`
  const helperTextId = `Filters.${key}.helperText`
  const appliedLabelId = `Filters.${key}.applied`

  const BooleanFilter = ({
    onChange,
    desktopScreen: _desktopScreen,
    value,
  }: FilterProps<boolean>) => {
    const intl = useIntl()
    const trackFilterUsage = useTrackFilterUsage<{
      filter_title: string
      is_selected: boolean
    }>('filters')

    const handleToggle = useCallback(
      (newValue: boolean) => {
        trackFilterUsage({ filter_title: filterTitle, is_selected: newValue })
        onChange(newValue)
      },
      [trackFilterUsage, onChange]
    )

    return (
      <div>
        <ToggleRow>
          <ScaledSwitch
            value={value}
            a11yLabel={intl.formatMessage({ id: toggleLabelId })}
            onToggle={handleToggle}
          />
          <ToggleLabel>
            <FormattedMessage id={toggleLabelId} />
          </ToggleLabel>
        </ToggleRow>
        <HelperText>
          <FormattedMessage id={helperTextId} />
        </HelperText>
      </div>
    )
  }

  BooleanFilter.label = label
  BooleanFilter.expandedOnInit = false
  BooleanFilter.isAlwaysDisplayed = true

  BooleanFilter.getOwnProps = ({
    state,
    updateFilters,
  }: Parameters<ListingFilter<boolean>['getOwnProps']>[0]) => ({
    filterKey: key,
    value: state.filters[key] === true,
    onChange: (value: boolean) => {
      const update: Partial<ListingFiltersState> = { [key]: value }
      updateFilters(update)
    },
  })

  BooleanFilter.Applied = function BooleanFilterApplied({
    filtersState,
    updateFilters,
    noClose,
  }: AppliedProps) {
    if (!filtersState[key]) return null

    return (
      <AppliedFilterSection
        label={label}
        values={[{ key, name: <FormattedMessage id={appliedLabelId} /> }]}
        onRemove={() => {
          const update: Partial<ListingFiltersState> = { [key]: false }
          updateFilters(update)
        }}
        noClose={noClose}
      />
    )
  }

  return BooleanFilter as unknown as ListingFilter<boolean>
}
